import { isRefreshable } from "@seagames/contracts";
import { type InfiniteData, infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import type { ContingentStayDefaults, NeedsRoomDayCounts, NeedsRoomPage, NeedsRoomState } from "@seagames/contracts";
import type {
	AccommodationSummary,
	CreateLodgingBuildingBody,
	DeskGuestPage,
	DeskLookup,
	FloorMap,
	FloorSaveResult,
	GenerateRoomsBody,
	GenerateRoomsResult,
	LodgingBuildingConfig,
	LodgingBuildingsPage,
	LodgingBuildingOption,
	LodgingRoomDetail,
	PatchLodgingBuildingBody,
	PatchRoomsBody,
	ReleaseReservationsResult,
	ReserveRoomsBody,
	ReserveRoomsResult,
	RoomAutoAssignResult,
	RoomAutoAssignSuggestion,
	RoomCheckAction,
	RoomCheckResult,
	RoomEditResult,
	RoomOption,
	SaveFloorBody,
	RoomingContingent,
	RoomingPeoplePage,
	RoomingPeopleSortKey,
	RoomingPersonState,
	StayGroupsResponse,
} from "../accommodation/types";
import { getAccessToken } from "../auth/token-store";
import { ApiError, api } from "./client";
import { saveBlob } from "./accreditations";
import { invalidateLogistics } from "./logistics-cache";

/**
 * Accommodation (M11): the host's reads and writes (PLAN §3.2) on the
 * contract's wire shapes (`@seagames/contracts` accommodation.ts) —
 * buildings, rooms, reservations, assignments, the desk and the rooming
 * lists. Every read is ONE request the API answers with one statement;
 * nothing here loops over rows. Keys live under `["accommodation", …]`, so
 * one invalidate refreshes every view (host, NOC, hotel — S5's reads share
 * the prefix).
 */

/** Drop unset keys so equal filters make equal query keys. */
const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "" && v !== null)) as T;

/** `GET /accommodation/contingents/:org/people` filters, wire form (`state` = one state or a comma list). */
export interface ContingentPeopleFilter {
	games_id: string;
	state?: RoomingPersonState | string;
	gender?: "MALE" | "FEMALE";
	lodging_building_id?: string;
	q?: string;
	sort?: RoomingPeopleSortKey;
	page?: number;
	page_size?: number;
}

/** The desk reads' filter (`/accommodation/arrivals|departures`). */
export interface DeskFilter {
	games_id: string;
	/** Absent = today in the games' zone. */
	date?: string;
	/** Comma list; absent = every building the viewer may see. */
	lodging_building_ids?: string;
}

/** List wrappers (`{ items }`) of the unpaged reads. */
export interface ItemsOf<T> {
	items: T[];
}

export const accommodationKeys = {
	all: ["accommodation"] as const,
	summary: (gamesId: string, date: string | undefined) => ["accommodation", "summary", gamesId, date ?? null] as const,
	contingents: (gamesId: string) => ["accommodation", "contingents", gamesId] as const,
	people: (orgId: string, f: ContingentPeopleFilter) => ["accommodation", "contingent", f.games_id, orgId, "people", clean(f)] as const,
	buildings: (gamesId: string, date: string | undefined) => ["accommodation", "buildings", gamesId, date ?? null] as const,
	buildingOptions: (gamesId: string) => ["accommodation", "buildings", "options", gamesId] as const,
	floorMap: (id: string, gamesId: string, date: string, block: string | undefined) => ["accommodation", "floor-map", id, gamesId, date, block ?? null] as const,
	room: (id: string, gamesId: string, date: string) => ["accommodation", "room", id, gamesId, date] as const,
	roomOptions: (acCardId: string, gamesId: string) => ["accommodation", "room-options", acCardId, gamesId] as const,
	desk: (direction: "arrivals" | "departures", f: DeskFilter) => ["accommodation", "desk", direction, clean(f)] as const,
	deskPeople: (direction: "arrivals" | "departures", f: DeskFilter, groupKey: string) =>
		["accommodation", "desk", direction, clean(f), "people", groupKey] as const,
	needsRoom: (f: NeedsRoomFilter) => ["accommodation", "needs-room", clean(f)] as const,
	needsRoomDayCounts: (gamesId: string, from: string, to: string) => ["accommodation", "needs-room", "day-counts", gamesId, from, to] as const,
};

/** `GET /accommodation/needs-room` filters, wire form. `date` absent = today in the games' zone. */
export interface NeedsRoomFilter {
	games_id: string;
	date?: string;
	state: NeedsRoomState;
	responsible_organization_id?: string;
	limit?: number;
}

// ── Reads ───────────────────────────────────────────────────────────────────

/** `GET /accommodation/summary` — the Overview strip and Needs attention: scalar subqueries in one statement. */
export const accommodationSummaryOptions = (gamesId: string, date?: string) =>
	queryOptions({
		queryKey: accommodationKeys.summary(gamesId, date),
		queryFn: ({ signal }) => api.get<AccommodationSummary>("/accommodation/summary", { query: clean({ games_id: gamesId, date }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/**
 * `GET /accommodation/needs-room` — "Arriving without a room" (GMS-2): keyset
 * "Load more"; the first page carries both tabs' totals and the
 * per-organisation counts. Host only (403 for anyone else).
 */
export const needsRoomOptions = (f: NeedsRoomFilter) =>
	infiniteQueryOptions<NeedsRoomPage, Error, InfiniteData<NeedsRoomPage>, ReturnType<typeof accommodationKeys.needsRoom>, string | undefined>({
		queryKey: accommodationKeys.needsRoom(f),
		queryFn: ({ signal, pageParam }) => api.get<NeedsRoomPage>("/accommodation/needs-room", { query: { ...clean(f), cursor: pageParam }, signal }),
		initialPageParam: undefined,
		getNextPageParam: (last) => last.next_cursor ?? undefined,
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** `GET /accommodation/needs-room/day-counts` — the Overview chips' badges: ONE request per visible window (≤ 14 days), keyed by it. */
export const needsRoomDayCountsOptions = (gamesId: string, from: string, to: string) =>
	queryOptions({
		queryKey: accommodationKeys.needsRoomDayCounts(gamesId, from, to),
		queryFn: ({ signal }) => api.get<NeedsRoomDayCounts>("/accommodation/needs-room/day-counts", { query: { games_id: gamesId, from, to }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** `GET /accommodation/contingents` — the Rooming table: one row per organisation in scope (≤ 500), one GROUP BY. */
export const contingentsOptions = (gamesId: string) =>
	queryOptions({
		queryKey: accommodationKeys.contingents(gamesId),
		queryFn: ({ signal }) => api.get<ItemsOf<RoomingContingent>>("/accommodation/contingents", { query: { games_id: gamesId }, signal }),
		staleTime: 30_000,
	});

/** `GET /accommodation/contingents/:org/people` — offset + `count(*) OVER()` (one organisation is bounded). */
export const contingentPeopleOptions = (orgId: string, f: ContingentPeopleFilter) =>
	queryOptions({
		queryKey: accommodationKeys.people(orgId, f),
		queryFn: ({ signal }) => api.get<RoomingPeoplePage>(`/accommodation/contingents/${orgId}/people`, { query: { ...clean(f) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});

/** `GET /lodging-buildings` — ≤ 200 rows, one GROUP BY (rooms, beds, out of service, reserved, occupied on `date`). A hotel gets its own. */
export const buildingsOptions = (gamesId: string, date?: string) =>
	queryOptions({
		queryKey: accommodationKeys.buildings(gamesId, date),
		queryFn: ({ signal }) => api.get<LodgingBuildingsPage>("/lodging-buildings", { query: clean({ games_id: gamesId, date }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** `GET /lodging-buildings/options` — pickers (helpdesk, the group's Hotels section). */
export const buildingOptionsOptions = (gamesId: string, enabled = true) =>
	queryOptions({
		queryKey: accommodationKeys.buildingOptions(gamesId),
		queryFn: ({ signal }) => api.get<LodgingBuildingOption[]>("/lodging-buildings/options", { query: { games_id: gamesId }, signal }),
		staleTime: 60_000,
		enabled,
	});

/** `GET /lodging-buildings/:id/floor-map` — one block's rooms with their taken bed slots on `date`. */
export const floorMapOptions = (id: string, gamesId: string, date: string, block?: string) =>
	queryOptions({
		queryKey: accommodationKeys.floorMap(id, gamesId, date, block),
		queryFn: ({ signal }) => api.get<FloorMap>(`/lodging-buildings/${id}/floor-map`, { query: clean({ games_id: gamesId, date, block }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});

/** `GET /lodging-rooms/:id` — the room sheet: reservation and occupants from `date` on. */
export const roomOptions = (id: string, gamesId: string, date: string) =>
	queryOptions({
		queryKey: accommodationKeys.room(id, gamesId, date),
		queryFn: ({ signal }) => api.get<LodgingRoomDetail>(`/lodging-rooms/${id}`, { query: { games_id: gamesId, date }, signal }),
		staleTime: 10_000,
	});

/** `GET /room-assignments/options?ac_card_id=` — free rooms for this person (see `RoomOption`: endpoint requested). */
export const freeRoomsOptions = (acCardId: string, gamesId: string, enabled = true) =>
	queryOptions({
		queryKey: accommodationKeys.roomOptions(acCardId, gamesId),
		queryFn: ({ signal }) => api.get<ItemsOf<RoomOption>>("/room-assignments/options", { query: { ac_card_id: acCardId, games_id: gamesId }, signal }),
		staleTime: 5_000,
		enabled,
	});

/** `GET /accommodation/arrivals|departures` — the desk's groups (by flight, else entry point + service, else no travel). Refetched every minute. */
export const deskGroupsOptions = (direction: "arrivals" | "departures", f: DeskFilter) =>
	queryOptions({
		queryKey: accommodationKeys.desk(direction, f),
		queryFn: ({ signal }) => api.get<StayGroupsResponse>(`/accommodation/${direction}`, { query: { ...clean(f) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
		refetchInterval: 60_000,
	});

/** `GET /accommodation/arrivals|departures/people?group_key=` — one group's guests (offset, ≤ 100 a page). */
export const deskPeopleOptions = (direction: "arrivals" | "departures", f: DeskFilter, groupKey: string, enabled = true, page = 1) =>
	queryOptions({
		queryKey: [...accommodationKeys.deskPeople(direction, f, groupKey), page] as const,
		queryFn: ({ signal }) =>
			api.get<DeskGuestPage>(`/accommodation/${direction}/people`, { query: { ...clean(f), group_key: groupKey, page, page_size: 100 }, signal }),
		staleTime: 10_000,
		enabled,
	});

/** `GET /room-assignments/lookup?ac_no=` — the desk scan: the card in scope, whether it may check in, its open assignment. */
export const deskLookup = (acNo: string, gamesId: string) => api.get<DeskLookup>("/room-assignments/lookup", { query: { ac_no: acNo, games_id: gamesId } });

/**
 * After an accommodation write: every read of the travel chain (`invalidateLogistics`) — every
 * accommodation read, A&D's readiness (a bed), the Contingent Dashboard and the record page (its
 * Accommodation section and logistics chain).
 */
export const invalidateAccommodation = invalidateLogistics;

// ── Writes ──────────────────────────────────────────────────────────────────

const after = async <T>(p: Promise<T>): Promise<T> => {
	const res = await p;
	await invalidateAccommodation();
	return res;
};

/** `POST /lodging-buildings`. Rooms come from `generateRooms` after. */
export const createBuilding = (body: CreateLodgingBuildingBody) => api.post<LodgingBuildingConfig>("/lodging-buildings", body);

/** `PATCH /lodging-buildings/:id`. */
export const patchBuilding = (id: string, body: PatchLodgingBuildingBody) => after(api.patch<LodgingBuildingConfig>(`/lodging-buildings/${id}`, body));

/** `DELETE /lodging-buildings/:id` — refused (409) while anything uses it. */
export const deleteBuilding = (id: string) => after(api.delete<unknown>(`/lodging-buildings/${id}`));

/** `POST /lodging-buildings/:id/rooms/generate` — blocks × floors × rooms per floor, one INSERT … SELECT; existing numbers skipped and counted. */
export const generateRooms = (id: string, body: GenerateRoomsBody) => after(api.post<GenerateRoomsResult>(`/lodging-buildings/${id}/rooms/generate`, body));

/** `PATCH /lodging-buildings/:id/rooms` — sex, out of service, beds for ≤ 500 rooms or whole floors; refusals listed per room. */
export const patchRooms = (buildingId: string, body: PatchRoomsBody) => after(api.patch<RoomEditResult>(`/lodging-buildings/${buildingId}/rooms`, body));

/** `PUT /lodging-buildings/:id/floors` — one floor as it should be: rename, add, remove (never-used rooms only), move the floor; `taken` = nothing written. */
export const saveFloor = (buildingId: string, body: SaveFloorBody) => after(api.put<FloorSaveResult>(`/lodging-buildings/${buildingId}/floors`, body));

/** `POST /accommodation/reservations` — whole rooms, women's rooms first; `short` = beds it could not find, per sex. */
export const reserveRooms = (body: ReserveRoomsBody) => after(api.post<ReserveRoomsResult>("/accommodation/reservations", body));

/** `POST /accommodation/reservations/release` — only rooms with nobody assigned for that games. */
export const releaseRooms = (gamesId: string, roomIds: string[]) =>
	after(api.post<ReleaseReservationsResult>("/accommodation/reservations/release", { games_id: gamesId, lodging_room_ids: roomIds }));

/** `PUT /accommodation/contingents/:org/stay-defaults` — both null clears; DEFAULT stays follow (trigger). */
export const putStayDefaults = (orgId: string, gamesId: string, stay: { arrive_on: string; depart_on: string } | null) =>
	after(
		api.put<ContingentStayDefaults>(`/accommodation/contingents/${orgId}/stay-defaults`, {
			games_id: gamesId,
			arrive_on: stay?.arrive_on ?? null,
			depart_on: stay?.depart_on ?? null,
		}),
	);

/** `POST /accommodation/auto-assign/suggest` — the rooms the system would pick; nothing is saved. */
export const suggestRooms = (gamesId: string, orgIds: string[], excludeCardIds: string[]) =>
	api.post<RoomAutoAssignSuggestion>("/accommodation/auto-assign/suggest", { games_id: gamesId, responsible_organization_ids: orgIds, exclude_card_ids: excludeCardIds });

/** `POST /accommodation/auto-assign` — the reviewed suggestion; an organisation whose picks moved is not saved (`changed`). */
export const autoAssignRooms = (gamesId: string, orgIds: string[], excludeCardIds: string[], fingerprints: Record<string, string>) =>
	after(
		api.post<RoomAutoAssignResult>("/accommodation/auto-assign", {
			games_id: gamesId,
			responsible_organization_ids: orgIds,
			exclude_card_ids: excludeCardIds,
			fingerprints,
		}),
	);

/** `POST /room-assignments` — a room (and optionally a bed / manual dates) for one approved card. */
export const assignRoom = (body: { ac_card_id: string; lodging_room_id: string; bed_no?: number; stay_from?: string; stay_to?: string }) =>
	after(api.post<unknown>("/room-assignments", body));

/** `PATCH /room-assignments/:id` — manual dates (stay becomes MANUAL), or follow travel again. */
export const patchAssignment = (id: string, body: { stay_from: string; stay_to: string } | { follow_travel: true }) =>
	after(api.patch<unknown>(`/room-assignments/${id}`, body));

/** `POST /room-assignments/:id/move` — a checked-in guest's night history is kept (closed + new row). */
export const moveAssignment = (id: string, body: { lodging_room_id: string; bed_no?: number }) =>
	after(api.post<unknown>(`/room-assignments/${id}/move`, body));

/** `DELETE /room-assignments/:id` — only before check-in. */
export const removeAssignment = (id: string) => after(api.delete<unknown>(`/room-assignments/${id}`));

/** `POST /room-assignments/check` — one UPDATE over ≤ 300 ids; each refusal named. UNDO_OUT takes one id. */
export const checkAssignments = (action: RoomCheckAction, ids: string[], via: "DESK" | "SCAN" = "DESK") =>
	after(api.post<RoomCheckResult>("/room-assignments/check", { action, room_assignment_ids: ids, via }));

// ── CSV ─────────────────────────────────────────────────────────────────────

const BASE_URL: string = import.meta.env.VITE_API_URL ?? "http://localhost:3001/api";

/**
 * `GET /accommodation/contingents/:org/people.csv` (same WHERE as the list,
 * streamed, ≤ 5,000 rows) → a file save, with the client's Bearer token and
 * one refresh retry — the same path as the records export.
 */
export async function downloadRoomingCsv(orgId: string, f: Omit<ContingentPeopleFilter, "page" | "page_size">, filename: string): Promise<void> {
	const url = new URL(`${BASE_URL}/accommodation/contingents/${orgId}/people.csv`, window.location.origin);
	for (const [k, v] of Object.entries(clean(f))) url.searchParams.set(k, String(v));
	const attempt = async (): Promise<{ blob: Blob } | { error: ApiError }> => {
		const token = getAccessToken();
		let res: Response;
		try {
			res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "omit" });
		} catch (err) {
			if (err instanceof DOMException && err.name === "AbortError") throw err;
			return { error: new ApiError("SERVICE_UNAVAILABLE", 0, "Can't reach the server right now.") };
		}
		if (res.ok) return { blob: await res.blob() };
		try {
			const body = (await res.json()) as { error?: { code?: ApiError["code"]; message?: string; details?: Record<string, unknown> } };
			if (body.error?.code) return { error: new ApiError(body.error.code, res.status, body.error.message ?? "", body.error.details) };
		} catch {
			/* not our envelope */
		}
		return { error: new ApiError("SERVICE_UNAVAILABLE", res.status, "Can't reach the server right now.") };
	};
	if (!getAccessToken()) await api.get("/auth/me");
	let out = await attempt();
	if ("error" in out && isRefreshable(out.error.code)) {
		await api.get("/auth/me");
		out = await attempt();
	}
	if ("error" in out) throw out.error;
	saveBlob(out.blob, filename);
}
